// Synchronous status projection between dispatches (including volley projectiles).
// A coherent status batch touches only its affected clusters; other dependencies use the possessor compiler.
import { C1_PROVISIONAL_STATUS_ID, NON_PC_ROOT_BUCKETS, c1ProvisionalStatusChainId, compilePossessor, compilePossessorStatusChain,
  type PossessorCompileContext, type PossessorStatusMutation } from './possessor-compile.js';
import { applyStatusRootFields, type StatusRootFields } from './status-root-residue.js';
import { recompileMemberStatusDiff } from './member-status-diff.js';
import { recompileMemberStatusDamageDefenses } from './member-status-damage-defenses.js';
export { recompileMemberStatusDiff } from './member-status-diff.js';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** One status change to replay on the member: an ADD (catalog id + caster) or a REMOVAL of a stored row (its status_id). */
export type MemberStatusStep = { kind: 'add'; statusItemId: string; casterId: string; stack?: number; statusId?: string } | { kind: 'remove'; statusItemId: string; statusId: string };
/** `addIds`: the provisional status_id each ADD step compiled under, in step order — the derived clusters (a skill's adv_dis_breakdown
 *  href_id …) cite those ids, so the caller stamps them on its pending rows until the commit assigns the real ones. */
export type MemberRecompileResult = { ok: true; creature: Row; vitality: Row; rootScalars: Row; rootFields?: StatusRootFields;
  rootBuckets?: Record<string, unknown[]>; catalog: Row | null; addIds: string[] } | { ok: false; reason: string };

/** Install into the caller-owned trial member. Root deletions must reach that same row before the next dispatch reads it. */
export function applyMemberStatusRoots(row: Row, result: Extract<MemberRecompileResult, { ok: true }>): void {
  for (const [key, value] of Object.entries(result.rootScalars)) { if (value === null) delete row[key]; else row[key] = value; }
  applyStatusRootFields(row, result.rootFields);
  if (result.rootBuckets) {
    const layers = (row.item_layer_types = { ...(isObj(row.item_layer_types) ? row.item_layer_types : {}) });
    for (const key of NON_PC_ROOT_BUCKETS) {
      const values = result.rootBuckets[key];
      if (values?.length) layers[key] = structuredClone(values); else delete layers[key];
    }
  }
}

/** The page scene a light / condition gate reads (websocket/possessor-scene.ts); absent fields → such a gate refuses by name, as before. */
export type MemberRecompileScene = Pick<PossessorCompileContext, 'lightTypesPresent' | 'lightTypesPresentAfter' | 'conditionScene'>;
export function recompileMemberStatuses(row: Row, steps: MemberStatusStep[], ctx: Pick<PossessorCompileContext, 'definition' | 'authoredDefinition' | 'catalog' | 'roster'> & MemberRecompileScene,
  options: { derivedOnly?: boolean } = {}): MemberRecompileResult {
  if (!steps.length) return { ok: false, reason: 'no-steps' };
  const ec = isObj(row.encounter_creatures) ? row.encounter_creatures : null;
  if (!ec) return { ok: false, reason: 'row-not-held' };
  const diff = recompileMemberStatusDiff(row, steps, ctx);
  if (diff) return diff;
  const defenses = recompileMemberStatusDamageDefenses(row, steps, ctx);
  if (defenses) return defenses;
  const targetId = upper(ec.encounter_creature_id);
  const savedIds = new Map<string, string>();
  let applied = 0;
  const mutations: PossessorStatusMutation[] = steps.map((step) => {
    if (step.kind === 'remove') return { kind: 'status', status_item_id: upper(step.statusItemId), applied: false, status_id: upper(step.statusId) };
    const id = steps.length === 1 ? C1_PROVISIONAL_STATUS_ID : c1ProvisionalStatusChainId(applied);
    applied += 1;
    if (step.statusId) savedIds.set(id, upper(step.statusId));
    return { kind: 'status', status_item_id: upper(step.statusItemId), applied: true, status_id: id,
      entry: { status_id: id, status_target_id: targetId, status_stack: step.stack ?? 1, status_item_id: upper(step.statusItemId),
        ...(upper(step.casterId) !== targetId ? { status_caster_id: upper(step.casterId) } : {}) } };
  });
  // R-116 (2026-09-30): a caller holding the encounter roster passes it — an owner-gated aura then compiles instead of refusing
  const compileCtx: PossessorCompileContext = { definition: ctx.definition, authoredDefinition: ctx.authoredDefinition, catalog: ctx.catalog ?? null, ...(ctx.roster ? { roster: ctx.roster } : {}),
    // fix-bug 2026-10-14: the scene of the light / condition gates (a light-gated passive refused every status change without it)
    ...(ctx.lightTypesPresent ? { lightTypesPresent: ctx.lightTypesPresent } : {}), ...(ctx.lightTypesPresentAfter ? { lightTypesPresentAfter: ctx.lightTypesPresentAfter } : {}),
    ...(ctx.conditionScene ? { conditionScene: ctx.conditionScene } : {}) };
  let result;
  try {
    result = mutations.length === 1 ? compilePossessor(row, mutations[0]!, compileCtx) : compilePossessorStatusChain(row, mutations, compileCtx, options);
  } catch (error) {
    return { ok: false, reason: `compile-threw:${String((error as Error)?.message ?? error).slice(0, 80)}` };
  }
  if (!result.ok) return { ok: false, reason: result.reason };
  // A committed attack projection already has real row ids; a trial volley keeps its provisional ones until its save returns.
  const bind = (value: unknown): unknown => {
    if (typeof value === 'string') return savedIds.get(upper(value)) ?? value;
    if (Array.isArray(value)) return value.map(bind);
    if (isObj(value)) return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, bind(child)]));
    return value;
  };
  const derived = savedIds.size ? bind(result.derived) as typeof result.derived : result.derived;
  const creature = derived['computed.creature'], vitality = derived['computed.vitality'];
  if (!isObj(creature) || !isObj(vitality)) return { ok: false, reason: 'derived-not-produced' };
  return { ok: true, creature, vitality, rootScalars: (derived.root_scalars ?? {}) as Row,
    ...(derived.root_fields ? { rootFields: derived.root_fields } : {}),
    ...(derived.root_buckets ? { rootBuckets: derived.root_buckets } : {}),
    catalog: isObj(derived.catalog) ? derived.catalog : null,
    addIds: mutations.filter((m) => m.applied).map((m) => savedIds.get(upper(m.status_id)) ?? upper(m.status_id)) };
}
